import { useState, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  adminApi,
  SERVICE_LINES,
  type AdminDocument,
  type Payout,
  type PayoutStatus,
  type ServiceLine,
  type Vessel,
} from '@/lib/adminApi';
import { ApiError } from '@/lib/api';
import { Card, Spinner, EmptyState, Badge, type BadgeTone } from '@/components/ui';
import { Drawer, Field } from '@/components/admin/Drawer';
import { WalletPanel } from '@/components/admin/WalletPanel';
import { BlockBanControl } from '@/components/admin/BlockBanControl';
import { ForceLogoutControl } from '@/components/admin/ForceLogoutControl';
import { PayoutKYCControl } from '@/components/admin/PayoutKYCControl';
import { AccountStatusBadge, DocStatusBadge } from '@/components/admin/shared';
import { personName, dateTime } from '@/lib/adminFormat';
import { useAdminPermission } from '@/lib/useAdminPermission';

type Tab = 'detail' | 'documents' | 'wallet' | 'payouts' | 'boats';

/** DriverDrawer — driver detail + vehicle + DOCUMENT REVIEW (approve/reject) +
 *  earnings wallet. The document-review controls are the headline write: each
 *  posts to admin-svc → driver-svc /sync, flipping status 16/17. */
export function DriverDrawer({ id, onClose }: { id: number; onClose: () => void }) {
  const [tab, setTab] = useState<Tab>('documents');
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['admin', 'driver', id],
    queryFn: ({ signal }) => adminApi.driver(id, signal),
  });

  const d = data?.driver;
  const v = data?.vehicle;

  return (
    <Drawer
      title={d ? personName(d) : `Driver #${id}`}
      subtitle={d ? `${d.country_code}${d.phone}` : undefined}
      onClose={onClose}
    >
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Spinner label="Loading driver…" />
        </div>
      ) : isError ? (
        <EmptyState title="Couldn't load driver" description={(error as Error)?.message} />
      ) : d ? (
        <div className="space-y-4">
          <Tabs tab={tab} onTab={setTab} docCount={data?.documents?.length ?? 0} />

          {tab === 'detail' ? (
            <div className="space-y-4">
              <div>
                <Field label="ID" value={`#${d.id}`} />
                <Field label="Name" value={personName(d)} />
                <Field label="Phone" value={`${d.country_code}${d.phone}`} />
                <Field label="Email" value={d.email || '—'} />
                <Field label="Status" value={<AccountStatusBadge status={d.status} />} />
                <Field label="Joined" value={dateTime(d.created_at)} />
              </div>
              <Card>
                <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted">Vehicle</div>
                {v ? (
                  <div>
                    <Field label="Make" value={v.brand || '—'} />
                    <Field label="Model" value={v.vehicle_name || '—'} />
                    <Field label="Plate" value={v.vehicle_number || '—'} />
                    <Field label="Colour" value={v.color || '—'} />
                    <Field label="Year" value={v.vehicle_year ?? '—'} />
                  </div>
                ) : (
                  <p className="text-xs text-muted">No vehicle on file (pre-onboarding).</p>
                )}
              </Card>
              {/* Service-line classification (the Drivers list "type" column;
                  gated by drivers.service_line — read-only for roles without it). */}
              <ServiceLineControl driverId={d.id} current={d.service_line} />
              {/* Trust & safety: ban / reinstate (gated by drivers.ban; hidden
                  for roles without it). A ban also drops dispatch eligibility. */}
              <BlockBanControl scope="driver" id={d.id} status={d.status} />
              {/* Force logout: revoke every session without changing status
                  (same drivers.ban gate; hidden for roles without it). */}
              <ForceLogoutControl scope="driver" id={d.id} />
            </div>
          ) : tab === 'documents' ? (
            <DocumentsReview driverId={d.id} documents={data?.documents ?? []} />
          ) : tab === 'wallet' ? (
            <WalletPanel scope="driver" userId={d.id} />
          ) : tab === 'payouts' ? (
            <PayoutsTab driverId={d.id} />
          ) : (
            <BoatsTab driverId={d.id} />
          )}
        </div>
      ) : (
        <EmptyState title="Driver not found" />
      )}
    </Drawer>
  );
}

/** ServiceLineControl — set a driver's service-line classification (the "type"
 *  the Drivers list shows). Gated by drivers.service_line; staff without it see
 *  the current value read-only. On save it invalidates the driver + list queries
 *  so the badge updates everywhere. */
function ServiceLineControl({ driverId, current }: { driverId: number; current?: string }) {
  const canManage = useAdminPermission('drivers.service_line');
  const qc = useQueryClient();
  const [value, setValue] = useState<ServiceLine | ''>((current as ServiceLine) || '');

  const mutation = useMutation({
    mutationFn: () => adminApi.setDriverServiceLine(driverId, value as ServiceLine),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'driver', driverId] });
      qc.invalidateQueries({ queryKey: ['admin', 'drivers'] });
    },
  });

  const label = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

  if (!canManage) {
    return (
      <Card>
        <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted">Service line</div>
        {current ? <Badge tone="info">{label(current)}</Badge> : <span className="text-xs text-muted">Not set</span>}
      </Card>
    );
  }

  const dirty = value !== ((current as ServiceLine) || '');

  return (
    <Card>
      <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted">Service line</div>
      <div className="flex items-center gap-2">
        <select
          value={value}
          onChange={(e) => setValue(e.target.value as ServiceLine | '')}
          className="input text-sm"
        >
          <option value="">— unclassified —</option>
          {SERVICE_LINES.map((s) => (
            <option key={s} value={s}>
              {label(s)}
            </option>
          ))}
        </select>
        <button
          type="button"
          disabled={!value || !dirty || mutation.isPending}
          onClick={() => mutation.mutate()}
          className="btn-primary"
        >
          {mutation.isPending ? 'Saving…' : 'Save'}
        </button>
        {mutation.isSuccess && !dirty ? <span className="text-xs text-ok">Saved.</span> : null}
        {mutation.isError ? (
          <span className="text-xs text-danger">{(mutation.error as Error)?.message}</span>
        ) : null}
      </div>
    </Card>
  );
}

/** The document list with per-document approve/reject controls (when the role
 *  may review — ops + superadmin; finance/support see them read-only). */
function DocumentsReview({ driverId, documents }: { driverId: number; documents: AdminDocument[] }) {
  // Only ops + superadmin hold documents.review; others view the docs read-only.
  // (admin-svc enforces this too — this just hides the approve/reject controls.)
  const canReview = useAdminPermission('documents.review');
  if (documents.length === 0) {
    return <EmptyState title="No documents" description="This driver has no document records yet. (Backfill may be pending.)" />;
  }
  // Group by category for a tidy list.
  const groups = ['driver', 'vehicle', 'accreditation'] as const;
  return (
    <div className="space-y-4">
      {!canReview ? (
        <p className="rounded-lg border border-ink-800 bg-ink-850 px-3 py-2 text-xs text-muted">
          Read-only — your role can view these documents but not approve or reject them.
          (Document review requires the <code className="text-slate-400">ops</code> role.)
        </p>
      ) : null}
      {groups.map((g) => {
        const docs = documents.filter((d) => d.category === g);
        if (docs.length === 0) return null;
        return (
          <div key={g}>
            <div className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-muted">{g}</div>
            <div className="space-y-2">
              {docs.map((doc) => (
                <DocumentRow key={doc.id} driverId={driverId} doc={doc} canReview={canReview} />
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function DocumentRow({ driverId, doc, canReview }: { driverId: number; doc: AdminDocument; canReview: boolean }) {
  const qc = useQueryClient();
  const [rejecting, setRejecting] = useState(false);
  const [reason, setReason] = useState(doc.rejection_reason ?? '');
  const [done, setDone] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: (decision: 'approve' | 'reject') =>
      adminApi.reviewDocument(driverId, doc.document_key, decision, decision === 'reject' ? reason : undefined),
    onSuccess: (res) => {
      setDone(res.message);
      setRejecting(false);
      // Refresh the driver detail so the badge + reason update.
      qc.invalidateQueries({ queryKey: ['admin', 'driver', driverId] });
    },
  });

  // The uploads bucket is private, so doc.path 403s on a direct GET. Click
  // "view file" to mint a short-lived presigned GET via admin-svc → uploads-svc,
  // then render it inline (image) or link out (PDF).
  const [signedUrl, setSignedUrl] = useState<string | null>(null);
  const isPdf = !!doc.path && /\.pdf(\?|$)/i.test(doc.path);
  const signMutation = useMutation({
    mutationFn: () => adminApi.signDocument(driverId, doc.path as string),
    onSuccess: (res) => setSignedUrl(res.url),
  });

  // Admin upload ON THE DRIVER'S BEHALF: presign → PUT the bytes straight to S3
  // with the signed headers → save the file_url (moves the doc to PENDING review).
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadErr, setUploadErr] = useState<string | null>(null);
  const handleFileSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // allow re-picking the same file
    if (!file) return;
    // uploads-svc only accepts jpg/jpeg/png/pdf AND requires content_type to match
    // the ext, so derive BOTH from the browser MIME type (canonical) — never trust
    // the filename extension alone (e.g. ".JPG" → image/jpeg → jpg).
    const MIME_EXT: Record<string, string> = {
      'image/jpeg': 'jpg',
      'image/png': 'png',
      'application/pdf': 'pdf',
    };
    const contentType = file.type;
    const ext = MIME_EXT[contentType];
    if (!ext) {
      setUploadErr('Only JPG, PNG or PDF files are allowed.');
      return;
    }
    setUploading(true);
    setUploadErr(null);
    setDone(null);
    try {
      const pre = await adminApi.presignDriverDocument(driverId, doc.category, contentType, ext);
      const putRes = await fetch(pre.upload_url, {
        method: pre.method || 'PUT',
        headers: pre.headers || {},
        body: file,
      });
      if (!putRes.ok) throw new Error(`Upload to storage failed (HTTP ${putRes.status})`);
      await adminApi.uploadDriverDocument(driverId, doc.category, doc.document_key, pre.file_url);
      setDone('Uploaded — now pending review.');
      qc.invalidateQueries({ queryKey: ['admin', 'driver', driverId] });
    } catch (err) {
      setUploadErr((err as Error)?.message || 'Upload failed. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  return (
    <Card>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="truncate text-sm font-medium text-slate-100">{doc.document_key}</div>
          <div className="mt-1 flex items-center gap-2">
            <DocStatusBadge status={doc.status} name={doc.status_name} />
            {doc.optional ? <Badge tone="neutral">optional</Badge> : null}
            {doc.path ? (
              signedUrl ? (
                <a href={signedUrl} target="_blank" rel="noreferrer" className="text-xs text-brand-400 hover:underline">
                  {isPdf ? 'open PDF ↗' : 'open full size ↗'}
                </a>
              ) : (
                <button
                  type="button"
                  onClick={() => signMutation.mutate()}
                  disabled={signMutation.isPending}
                  className="text-xs text-brand-400 hover:underline disabled:opacity-50"
                >
                  {signMutation.isPending ? 'loading…' : 'view file'}
                </button>
              )
            ) : (
              <span className="text-xs text-muted">not uploaded</span>
            )}
          </div>
          {doc.status === 17 && doc.rejection_reason ? (
            <p className="mt-1 text-xs text-danger">Reason: {doc.rejection_reason}</p>
          ) : null}
          {signedUrl && !isPdf ? (
            <img
              src={signedUrl}
              alt={`${doc.document_key} document`}
              className="mt-2 max-h-72 w-auto rounded-md ring-1 ring-white/10"
            />
          ) : null}
          {signMutation.isError ? (
            <p className="mt-1 text-xs text-danger">Couldn't load this document — try again.</p>
          ) : null}
        </div>
        {canReview ? (
          <div className="flex shrink-0 flex-col items-end gap-1.5">
            <div className="flex gap-1.5">
              <button
                type="button"
                disabled={mutation.isPending}
                onClick={() => {
                  setDone(null);
                  mutation.mutate('approve');
                }}
                className="rounded-md bg-ok/15 px-2.5 py-1 text-xs font-medium text-ok ring-1 ring-inset ring-ok/30 hover:bg-ok/25 disabled:opacity-50"
              >
                Approve
              </button>
              <button
                type="button"
                disabled={mutation.isPending}
                onClick={() => {
                  setDone(null);
                  setRejecting((v) => !v);
                }}
                className="rounded-md bg-danger/15 px-2.5 py-1 text-xs font-medium text-danger ring-1 ring-inset ring-danger/30 hover:bg-danger/25 disabled:opacity-50"
              >
                Reject
              </button>
            </div>
            {/* Staff upload ON THE DRIVER'S BEHALF: presign → browser PUT → save. */}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,application/pdf"
              className="hidden"
              onChange={handleFileSelected}
            />
            <button
              type="button"
              disabled={uploading}
              onClick={() => fileInputRef.current?.click()}
              className="rounded-md bg-brand-600/15 px-2.5 py-1 text-xs font-medium text-brand-400 ring-1 ring-inset ring-brand-400/30 hover:bg-brand-600/25 disabled:opacity-50"
            >
              {uploading ? 'Uploading…' : doc.path ? 'Replace file' : 'Upload file'}
            </button>
          </div>
        ) : null}
      </div>

      {canReview && rejecting ? (
        <div className="mt-2 flex items-end gap-2">
          <div className="min-w-0 flex-1">
            <label className="block text-[11px] text-muted">Rejection reason (shown to the driver)</label>
            <input
              type="text"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. licence expired / photo blurry"
              className="mt-1 input"
            />
          </div>
          <button
            type="button"
            disabled={!reason.trim() || mutation.isPending}
            onClick={() => mutation.mutate('reject')}
            className="rounded-md bg-danger px-3 py-1.5 text-xs font-semibold text-white hover:bg-danger/90 disabled:opacity-50"
          >
            {mutation.isPending ? 'Rejecting…' : 'Confirm reject'}
          </button>
        </div>
      ) : null}

      {done ? <p className="mt-2 text-xs text-ok">{done}</p> : null}
      {mutation.isError ? <p className="mt-2 text-xs text-danger">{(mutation.error as Error)?.message}</p> : null}
      {uploadErr ? <p className="mt-2 text-xs text-danger">{uploadErr}</p> : null}
    </Card>
  );
}

function Tabs({ tab, onTab, docCount }: { tab: Tab; onTab: (t: Tab) => void; docCount: number }) {
  const item = (key: Tab, label: string) => (
    <button
      type="button"
      onClick={() => onTab(key)}
      className={`rounded-md px-3 py-1.5 text-xs font-medium transition-colors ${
        tab === key ? 'bg-brand-600/15 text-brand-400' : 'text-muted hover:bg-ink-800 hover:text-slate-200'
      }`}
    >
      {label}
    </button>
  );
  return (
    <div className="flex gap-1 rounded-lg border border-ink-800 bg-ink-850 p-1">
      {item('detail', 'Detail')}
      {item('documents', `Documents${docCount ? ` (${docCount})` : ''}`)}
      {item('wallet', 'Wallet')}
      {item('payouts', 'Payouts & KYC')}
      {item('boats', 'Boats')}
    </div>
  );
}

// Payout status → badge tone (mirrors the Payouts page STATUS_TONE).
const PAYOUT_STATUS_TONE: Record<PayoutStatus, BadgeTone> = {
  PENDING: 'info',
  CREATED: 'info',
  SETTLED: 'ok',
  FAILED: 'danger',
  RETURNED: 'warn',
};

function payoutAud(n: number): string {
  return `$${(n ?? 0).toFixed(2)}`;
}

/** PayoutsTab — the driver's Azupay payout history + the payout-KYC verify
 *  control. Reads admin-svc GET /v1/admin/payouts filtered to this driver
 *  (wallet-svc owns the driver_payouts table; admin-svc only READS it). The
 *  list degrades to a clean "not yet in Postgres" empty state pre-cutover. */
function PayoutsTab({ driverId }: { driverId: number }) {
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['admin', 'driver-payouts', driverId],
    queryFn: ({ signal }) => adminApi.payouts({ driver_id: driverId, limit: 25 }, signal),
  });

  const unavailable = data && data.available === false;
  const items: Payout[] = data?.items ?? [];

  return (
    <div className="space-y-4">
      {/* Payout-KYC verify (gated; currently a clean "not wired yet"). Shown
          first so the headline action is at the top of the tab. */}
      <PayoutKYCControl driverId={driverId} />

      <div>
        <div className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-muted">Payout history</div>
        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <Spinner label="Loading payouts…" />
          </div>
        ) : isError ? (
          <EmptyState title="Couldn't load payouts" description={(error as Error)?.message} />
        ) : unavailable ? (
          <EmptyState
            title="Payouts not yet in Postgres"
            description={data?.note ?? 'The driver payout ledger is owned by wallet-svc and is not in this Postgres yet.'}
          />
        ) : items.length === 0 ? (
          <EmptyState title="No payouts" description="This driver hasn't requested a cash-out yet." />
        ) : (
          <div className="space-y-2">
            {items.map((p) => (
              <PayoutRow key={p.id} payout={p} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/** One compact payout card in the driver drawer's payout history list. */
function PayoutRow({ payout: p }: { payout: Payout }) {
  return (
    <Card>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <Badge tone={PAYOUT_STATUS_TONE[p.status] ?? 'neutral'}>{p.status_name || p.status}</Badge>
            <span className="text-xs text-muted">{p.payment_method === 'payid' ? 'PayID' : 'Bank (BSB)'}</span>
          </div>
          <div className="mt-1 text-xs text-muted">{dateTime(p.created_at)}</div>
          {p.failure_reason ? <p className="mt-1 text-xs text-danger">Reason: {p.failure_reason}</p> : null}
        </div>
        <div className="shrink-0 text-right">
          <div className="metric text-sm text-slate-100">{payoutAud(p.amount)}</div>
          <div className="metric text-[11px] text-ok" title={`fee ${payoutAud(p.platform_fee)}`}>
            net {payoutAud(p.net_amount)}
          </div>
        </div>
      </div>
    </Card>
  );
}

/** BoatsTab — Telride Boats captain management for one driver. DARK until the
 *  Sept-2026 launch (boat_capable defaults false fleet-wide; no trip_type 15
 *  trips). Toggle the boat-capable flag + manage the captain's vessels (add/edit
 *  + boat photo & charter-cert upload via the SAME presign→S3 flow the document
 *  review uses). Writes gated by drivers.service_line (admin-svc enforces it on
 *  the boat-capable + vessel endpoints); drivers.read is enough to view. */
function BoatsTab({ driverId }: { driverId: number }) {
  const canManage = useAdminPermission('drivers.service_line');
  const qc = useQueryClient();
  const [editing, setEditing] = useState<Vessel | 'new' | null>(null);

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['admin', 'driver-vessels', driverId],
    queryFn: ({ signal }) => adminApi.vessels(driverId, signal),
  });

  const toggle = useMutation({
    mutationFn: (next: boolean) => adminApi.setBoatCapable(driverId, next),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'driver-vessels', driverId] }),
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Spinner label="Loading boats…" />
      </div>
    );
  }
  // A 503 means admin-svc can't reach driver-svc's boats endpoints yet (client
  // unconfigured) — surface it as a calm "not configured", not a red error.
  const apiErr = error instanceof ApiError ? (error as ApiError) : null;
  if (isError && apiErr?.status === 503) {
    return <EmptyState title="Boats not configured yet" description="Telride Boats management isn't wired on this environment yet." />;
  }
  if (isError) {
    return <EmptyState title="Couldn't load boats" description={(error as Error)?.message} />;
  }

  const capable = data?.boat_capable ?? false;
  const vessels = data?.vessels ?? [];

  return (
    <div className="space-y-4">
      <Card>
        <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted">Boat captain</div>
        {canManage ? (
          <label className="flex items-center gap-2 text-sm text-slate-200">
            <input
              type="checkbox"
              checked={capable}
              disabled={toggle.isPending}
              onChange={(e) => toggle.mutate(e.target.checked)}
              className="h-4 w-4 rounded border-ink-700 bg-ink-900 text-brand-500"
            />
            {capable ? 'Approved as a Telride Boats captain' : 'Not a boats captain'}
            {toggle.isPending ? <span className="text-xs text-muted">saving…</span> : null}
          </label>
        ) : capable ? (
          <Badge tone="ok">Boats captain</Badge>
        ) : (
          <span className="text-xs text-muted">Not a boats captain</span>
        )}
        {toggle.isError ? <p className="mt-1 text-xs text-danger">{(toggle.error as Error)?.message}</p> : null}
        <p className="mt-2 text-[11px] text-muted">
          Telride Boats launches Sept 2026 — this flag and the vessels below are inert until then.
        </p>
      </Card>

      <div>
        <div className="mb-1.5 flex items-center justify-between">
          <div className="text-xs font-semibold uppercase tracking-wide text-muted">Vessels</div>
          {canManage ? (
            <button type="button" onClick={() => setEditing('new')} className="btn-secondary text-xs">
              + Add vessel
            </button>
          ) : null}
        </div>
        {vessels.length === 0 ? (
          <EmptyState title="No vessels" description="This captain has no boats on file yet." />
        ) : (
          <div className="space-y-2">
            {vessels.map((ves) => (
              <VesselRow key={ves.id} driverId={driverId} vessel={ves} canManage={canManage} onEdit={() => setEditing(ves)} />
            ))}
          </div>
        )}
      </div>

      {editing ? (
        <VesselDrawer
          driverId={driverId}
          vessel={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            qc.invalidateQueries({ queryKey: ['admin', 'driver-vessels', driverId] });
            setEditing(null);
          }}
        />
      ) : null}
    </div>
  );
}

/** One vessel card with lazily-presigned view links for the boat photo + charter
 *  cert (the uploads bucket is private — same pattern as DocumentRow). */
function VesselRow({
  driverId,
  vessel,
  canManage,
  onEdit,
}: {
  driverId: number;
  vessel: Vessel;
  canManage: boolean;
  onEdit: () => void;
}) {
  const [imgUrl, setImgUrl] = useState<string | null>(null);
  const [certUrl, setCertUrl] = useState<string | null>(null);
  const signImg = useMutation({
    mutationFn: () => adminApi.signDocument(driverId, vessel.image_path as string),
    onSuccess: (res) => setImgUrl(res.url),
  });
  const signCert = useMutation({
    mutationFn: () => adminApi.signDocument(driverId, vessel.charter_cert_path as string),
    onSuccess: (res) => setCertUrl(res.url),
  });
  return (
    <Card>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="truncate text-sm font-medium text-slate-100">{vessel.name || `Vessel #${vessel.id}`}</div>
          <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-muted">
            <Badge tone={vessel.status === 1 ? 'ok' : 'neutral'}>{vessel.status === 1 ? 'active' : 'inactive'}</Badge>
            {vessel.registration ? <span>Reg {vessel.registration}</span> : null}
            {vessel.capacity ? <span>· {vessel.capacity} pax</span> : null}
            {vessel.insurance_expiry ? <span>· ins. {vessel.insurance_expiry.slice(0, 10)}</span> : null}
          </div>
          <div className="mt-1.5 flex flex-wrap items-center gap-3">
            {vessel.image_path ? (
              imgUrl ? (
                <a href={imgUrl} target="_blank" rel="noreferrer" className="text-xs text-brand-400 hover:underline">
                  boat photo ↗
                </a>
              ) : (
                <button
                  type="button"
                  onClick={() => signImg.mutate()}
                  disabled={signImg.isPending}
                  className="text-xs text-brand-400 hover:underline disabled:opacity-50"
                >
                  {signImg.isPending ? 'loading…' : 'view boat photo'}
                </button>
              )
            ) : null}
            {vessel.charter_cert_path ? (
              certUrl ? (
                <a href={certUrl} target="_blank" rel="noreferrer" className="text-xs text-brand-400 hover:underline">
                  charter cert ↗
                </a>
              ) : (
                <button
                  type="button"
                  onClick={() => signCert.mutate()}
                  disabled={signCert.isPending}
                  className="text-xs text-brand-400 hover:underline disabled:opacity-50"
                >
                  {signCert.isPending ? 'loading…' : 'view charter cert'}
                </button>
              )
            ) : null}
          </div>
        </div>
        {canManage ? (
          <button type="button" onClick={onEdit} className="shrink-0 text-xs text-brand-400 hover:underline">
            Edit
          </button>
        ) : null}
      </div>
    </Card>
  );
}

/** VesselDrawer — add or edit one vessel. The boat PHOTO uploads under the
 *  'vehicle' scope and the charter CERT under 'accreditation' (uploads-svc only
 *  accepts driver|vehicle|accreditation). We presign → browser-PUT the bytes to
 *  S3 → stash the returned file_url onto the vessel row (NOT a driver-documents
 *  row — saveVessel persists the pointer). */
function VesselDrawer({
  driverId,
  vessel,
  onClose,
  onSaved,
}: {
  driverId: number;
  vessel: Vessel | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [name, setName] = useState(vessel?.name ?? '');
  const [registration, setRegistration] = useState(vessel?.registration ?? '');
  const [capacity, setCapacity] = useState(vessel?.capacity ? String(vessel.capacity) : '');
  const [insuranceExpiry, setInsuranceExpiry] = useState((vessel?.insurance_expiry ?? '').slice(0, 10));
  const [status, setStatus] = useState(vessel?.status ?? 1);
  const [imagePath, setImagePath] = useState<string | null>(vessel?.image_path ?? null);
  const [certPath, setCertPath] = useState<string | null>(vessel?.charter_cert_path ?? null);
  const [uploadErr, setUploadErr] = useState<string | null>(null);
  const [uploading, setUploading] = useState<'image' | 'cert' | null>(null);

  const save = useMutation({
    mutationFn: () =>
      adminApi.saveVessel(driverId, {
        id: vessel?.id ?? 0,
        name: name.trim(),
        registration: registration.trim(),
        capacity: Number(capacity) || 0,
        insurance_expiry: insuranceExpiry || '',
        status: Number(status),
        image_path: imagePath,
        charter_cert_path: certPath,
      }),
    onSuccess: onSaved,
  });

  const MIME_EXT: Record<string, string> = { 'image/jpeg': 'jpg', 'image/png': 'png', 'application/pdf': 'pdf' };
  const uploadFor = (kind: 'image' | 'cert') => async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    const ext = MIME_EXT[file.type];
    if (!ext) {
      setUploadErr('Only JPG, PNG or PDF files are allowed.');
      return;
    }
    // Boat photo → 'vehicle' scope; charter cert → 'accreditation' (the only
    // scopes uploads-svc accepts besides 'driver' — do NOT invent 'boat').
    const scope = kind === 'image' ? 'vehicle' : 'accreditation';
    setUploading(kind);
    setUploadErr(null);
    try {
      const pre = await adminApi.presignDriverDocument(driverId, scope, file.type, ext);
      const putRes = await fetch(pre.upload_url, { method: pre.method || 'PUT', headers: pre.headers || {}, body: file });
      if (!putRes.ok) throw new Error(`Upload to storage failed (HTTP ${putRes.status})`);
      if (kind === 'image') setImagePath(pre.file_url);
      else setCertPath(pre.file_url);
    } catch (err) {
      setUploadErr((err as Error)?.message || 'Upload failed. Please try again.');
    } finally {
      setUploading(null);
    }
  };

  return (
    <Drawer title={vessel ? 'Edit vessel' : 'Add vessel'} onClose={onClose}>
      <div className="space-y-3">
        <LabeledInput label="Name" value={name} onChange={setName} placeholder="e.g. Harbour Pearl" />
        <LabeledInput label="Registration" value={registration} onChange={setRegistration} placeholder="e.g. AB-1234" />
        <LabeledInput label="Capacity (passengers)" value={capacity} onChange={setCapacity} type="number" />
        <LabeledInput label="Insurance expiry" value={insuranceExpiry} onChange={setInsuranceExpiry} type="date" />
        <div>
          <label className="block text-[11px] text-muted">Status</label>
          <select value={status} onChange={(e) => setStatus(Number(e.target.value))} className="mt-1 input text-sm">
            <option value={1}>Active</option>
            <option value={0}>Inactive</option>
          </select>
        </div>
        <UploadField label="Boat photo" current={imagePath} busy={uploading === 'image'} onPick={uploadFor('image')} accept="image/jpeg,image/png" />
        <UploadField
          label="Charter certificate"
          current={certPath}
          busy={uploading === 'cert'}
          onPick={uploadFor('cert')}
          accept="image/jpeg,image/png,application/pdf"
        />
        {uploadErr ? <p className="text-xs text-danger">{uploadErr}</p> : null}
        <div className="flex items-center gap-2 pt-2">
          <button
            type="button"
            disabled={!name.trim() || save.isPending || uploading !== null}
            onClick={() => save.mutate()}
            className="btn-primary"
          >
            {save.isPending ? 'Saving…' : 'Save vessel'}
          </button>
          <button type="button" onClick={onClose} className="btn-secondary">
            Cancel
          </button>
          {save.isError ? <span className="text-xs text-danger">{(save.error as Error)?.message}</span> : null}
        </div>
      </div>
    </Drawer>
  );
}

function LabeledInput({
  label,
  value,
  onChange,
  placeholder,
  type = 'text',
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  type?: string;
}) {
  return (
    <div>
      <label className="block text-[11px] text-muted">{label}</label>
      <input
        type={type}
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1 input text-sm"
      />
    </div>
  );
}

function UploadField({
  label,
  current,
  busy,
  onPick,
  accept,
}: {
  label: string;
  current: string | null;
  busy: boolean;
  onPick: (e: React.ChangeEvent<HTMLInputElement>) => void;
  accept: string;
}) {
  const ref = useRef<HTMLInputElement>(null);
  return (
    <div>
      <label className="block text-[11px] text-muted">{label}</label>
      <div className="mt-1 flex items-center gap-2">
        <input ref={ref} type="file" accept={accept} className="hidden" onChange={onPick} />
        <button type="button" disabled={busy} onClick={() => ref.current?.click()} className="btn-secondary text-xs disabled:opacity-50">
          {busy ? 'Uploading…' : current ? 'Replace file' : 'Upload file'}
        </button>
        {current ? <span className="text-xs text-ok">uploaded ✓</span> : <span className="text-xs text-muted">none</span>}
      </div>
    </div>
  );
}
